import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { RDS_DIALOG_DATA, RdsDialogRef } from '@rds/angular-components';
import { SpdgUserService } from '@spdg/core';
import { Subject, takeUntil } from 'rxjs';

import { NotificationPreference } from 'src/app/model/notification-preference.model';
import { UserService } from 'src/app/service/user.service';

@Component({
    selector: 'app-profile-settings',
    templateUrl: './profile-settings.component.html',
    styleUrls: ['./profile-settings.component.scss'],
    standalone: false,
})
export class ProfileSettingsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    // UI state
    activeSection: 'profile' | 'notifications' = 'profile';
    isLeftSectionCollapsed = false;

    // Profile fields
    email = '';
    firstName = '';
    lastName = '';

    // Notification settings
    notificationPreference: NotificationPreference = 'MENTIONS_ONLY';
    originalNotificationPreference: NotificationPreference = 'MENTIONS_ONLY';
    hasNotificationChanges = false;
    isSaving = false;

    constructor(
        @Optional() @Inject(RDS_DIALOG_DATA) public data: any,
        @Optional()
        private readonly dialogRef: RdsDialogRef<ProfileSettingsComponent>,
        private readonly spdgUserService: SpdgUserService,
        private readonly userService: UserService
    ) {}

    ngOnInit(): void {
        this.loadUserProfile();
        this.loadNotificationPreference();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadUserProfile(): void {
        this.spdgUserService.userIdentity
            .pipe(takeUntil(this.destroy$))
            .subscribe((identity) => {
                if (identity) {
                    this.firstName = identity.givenName || '';
                    this.lastName = identity.lastName || '';
                    this.email = identity.email || '';
                }
            });
    }

    private loadNotificationPreference(): void {
        this.userService.notificationPreference$
            .pipe(takeUntil(this.destroy$))
            .subscribe((preference) => {
                this.notificationPreference = preference;
                this.originalNotificationPreference = preference;
            });
    }

    setActiveSection(section: 'profile' | 'notifications'): void {
        this.activeSection = section;
    }

    toggleLeftSection(): void {
        this.isLeftSectionCollapsed = !this.isLeftSectionCollapsed;
    }

    onNotificationChange(): void {
        this.hasNotificationChanges =
            this.notificationPreference !== this.originalNotificationPreference;
    }

    saveNotificationSettings(): void {
        this.isSaving = true;
        this.userService
            .updateNotificationPreference(this.notificationPreference)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.is_success) {
                        this.originalNotificationPreference =
                            this.notificationPreference;
                        this.hasNotificationChanges = false;
                        this.closeDialog();
                    }
                    this.isSaving = false;
                },
                error: () => {
                    this.isSaving = false;
                },
            });
    }

    cancelNotificationChanges(): void {
        this.notificationPreference = this.originalNotificationPreference;
        this.hasNotificationChanges = false;
    }

    closeDialog(): void {
        this.dialogRef?.close();
    }
}
